/**
 * Coopital Voting Modal
 * 
 * Displays a cooperative ballot (tryBeforePay or marginRate) where
 * coop members grade candidate values using Excellent/Good/Acceptable/Poor/Failure.
 * Each voter's grade is weighted by their coopital share.
 * Includes write-in support to propose new candidate values.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { X, Plus, Loader2 } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';

type BallotType = 'tryBeforePay' | 'marginRate';
type Grade = 'A' | 'B' | 'C' | 'D' | 'F';

interface GradeCounts {
  A: number;
  B: number;
  C: number;
  D: number;
  F: number;
}

interface BallotOption {
  value: number;
  gradeCounts: GradeCounts;
  voterCount: number;
  median: string;
}

interface BallotData {
  question: string;
  options: BallotOption[];
  winningValue: number;
  userVotes: Record<string, Grade>;
  userVoteWeight: number;
}

const GRADE_CONFIG: Array<{grade: Grade; label: string; color: string; selectedColor: string}> = [
  {grade: 'A', label: 'Excellent', color: 'text-green-500/50', selectedColor: 'bg-green-600 text-white'},
  {grade: 'B', label: 'Good', color: 'text-teal-500/50', selectedColor: 'bg-teal-600 text-white'},
  {grade: 'C', label: 'Acceptable', color: 'text-yellow-500/50', selectedColor: 'bg-yellow-600 text-white'},
  {grade: 'D', label: 'Poor', color: 'text-orange-500/50', selectedColor: 'bg-orange-600 text-white'},
  {grade: 'F', label: 'Failure', color: 'text-red-500/50', selectedColor: 'bg-red-600 text-white'},
];

const MEDIAN_LABELS: Record<string, string> = {
  A: 'Excellent', B: 'Good', C: 'Acceptable', D: 'Poor', F: 'Failure',
};

interface CoopitalVotingModalProps {
  ballotType: BallotType;
  onClose: () => void;
}

export const CoopitalVotingModal: React.FC<CoopitalVotingModalProps> = ({
  ballotType,
  onClose,
}) => {
  const [ballotData, setBallotData] = useState<BallotData | null>(null);
  const [loading, setLoading] = useState(true);
  const [votingValue, setVotingValue] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Write-in state
  const [showWriteIn, setShowWriteIn] = useState(false);
  const [writeInValue, setWriteInValue] = useState('');
  const [writeInGrade, setWriteInGrade] = useState<Grade | null>(null);
  const [submittingWriteIn, setSubmittingWriteIn] = useState(false);

  const fetchBallot = useCallback(async () => {
    try {
      const functions = getFunctions();
      const getBallot = httpsCallable(functions, 'getCoopitalBallot');
      const result = await getBallot({ballotType});
      const data = result.data as any;
      if (data.success) {
        setBallotData({
          question: data.question,
          options: data.options,
          winningValue: data.winningValue,
          userVotes: data.userVotes || {},
          userVoteWeight: data.userVoteWeight || 0,
        });
      } else {
        setError(data.error || 'Failed to load ballot');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load ballot');
    } finally {
      setLoading(false);
    }
  }, [ballotType]);

  useEffect(() => {
    fetchBallot();
  }, [fetchBallot]);

  const handleVote = async (value: number, grade: Grade) => {
    setVotingValue(value);
    setError(null);
    try {
      const functions = getFunctions();
      const cast = httpsCallable(functions, 'castCoopitalVote');
      const result = await cast({ballotType, value, grade});
      const data = result.data as any;
      if (data.success) {
        // Update local state optimistically
        setBallotData((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            winningValue: data.winningValue,
            userVotes: {...prev.userVotes, [String(value)]: grade},
          };
        });
        // Refetch for accurate counts
        await fetchBallot();
      } else {
        setError(data.error || 'Vote failed');
      }
    } catch (err: any) {
      setError(err.message || 'Vote failed');
    } finally {
      setVotingValue(null);
    }
  };

  const handleWriteIn = async () => {
    const numValue = parseInt(writeInValue, 10);
    if (isNaN(numValue) || numValue < 0 || !Number.isInteger(numValue)) {
      setError('Please enter a non-negative whole number');
      return;
    }
    if (!writeInGrade) {
      setError('Please select a grade for your write-in');
      return;
    }

    setSubmittingWriteIn(true);
    setError(null);
    try {
      const functions = getFunctions();
      const addWriteIn = httpsCallable(functions, 'addCoopitalWriteIn');
      const result = await addWriteIn({
        ballotType,
        value: numValue,
        grade: writeInGrade,
      });
      const data = result.data as any;
      if (data.success) {
        setShowWriteIn(false);
        setWriteInValue('');
        setWriteInGrade(null);
        await fetchBallot();
      } else {
        setError(data.error || 'Write-in failed');
      }
    } catch (err: any) {
      setError(err.message || 'Write-in failed');
    } finally {
      setSubmittingWriteIn(false);
    }
  };

  const unitLabel = ballotType === 'marginRate' ? '%' : '';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
      <div className="bg-background border border-border rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto">
        <div className="p-6 space-y-4">
          {/* Header */}
          <div className="flex items-start justify-between">
            <h2 className="text-lg font-semibold pr-4">
              {ballotType === 'tryBeforePay' ? 'Try Before Pay' : 'Coopital Margin'} Vote
            </h2>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : ballotData ? (
            <>
              {/* Question */}
              <p className="text-sm text-muted-foreground">
                {ballotData.question}
              </p>

              <p className="text-sm font-medium">Please vote:</p>

              {/* Options */}
              <div className="space-y-3">
                {ballotData.options.map((option) => {
                  const isWinner = option.value === ballotData.winningValue;
                  const userGrade = ballotData.userVotes[String(option.value)];
                  const isVoting = votingValue === option.value;

                  return (
                    <div
                      key={option.value}
                      className={`rounded-lg border p-3 space-y-2 ${
                        isWinner
                          ? 'border-green-500/50 bg-green-500/5'
                          : 'border-border'
                      }`}
                    >
                      {/* Value + median */}
                      <div className="flex items-center justify-between">
                        <span className="font-mono font-semibold text-sm">
                          {option.value}{unitLabel}
                          {isWinner && (
                            <span className="ml-2 text-xs text-green-500 font-sans font-normal">
                              winning
                            </span>
                          )}
                        </span>
                        {option.voterCount > 0 && (
                          <span className="text-xs text-muted-foreground">
                            median: {MEDIAN_LABELS[option.median] || option.median}
                            {' '}({option.voterCount} voter{option.voterCount !== 1 ? 's' : ''})
                          </span>
                        )}
                      </div>

                      {/* Grade buttons */}
                      <div className="flex gap-1.5 flex-wrap">
                        {GRADE_CONFIG.map(({grade, label, color, selectedColor}) => {
                          const isSelected = userGrade === grade;
                          return (
                            <button
                              key={grade}
                              onClick={() => handleVote(option.value, grade)}
                              disabled={isVoting}
                              className={`text-xs px-2 py-1 rounded transition-all ${
                                isSelected
                                  ? selectedColor
                                  : `border border-current ${color} hover:opacity-80`
                              } ${isVoting ? 'opacity-50' : ''}`}
                            >
                              {isVoting && isSelected ? (
                                <Loader2 className="h-3 w-3 animate-spin inline" />
                              ) : (
                                label
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Write-in */}
              {showWriteIn ? (
                <div className="rounded-lg border border-dashed border-border p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={writeInValue}
                      onChange={(e) => setWriteInValue(e.target.value)}
                      placeholder={ballotType === 'marginRate' ? 'e.g. 5' : 'e.g. 3'}
                      className="w-20 text-sm px-2 py-1 rounded border border-border bg-background font-mono"
                    />
                    {ballotType === 'marginRate' && (
                      <span className="text-sm text-muted-foreground">%</span>
                    )}
                  </div>
                  <div className="flex gap-1.5 flex-wrap">
                    {GRADE_CONFIG.map(({grade, label, color, selectedColor}) => (
                      <button
                        key={grade}
                        onClick={() => setWriteInGrade(grade)}
                        className={`text-xs px-2 py-1 rounded transition-all ${
                          writeInGrade === grade
                            ? selectedColor
                            : `border border-current ${color} hover:opacity-80`
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleWriteIn}
                      disabled={submittingWriteIn || !writeInValue || !writeInGrade}
                      className="text-xs px-3 py-1 rounded bg-foreground text-background hover:opacity-80 transition-opacity disabled:opacity-30"
                    >
                      {submittingWriteIn ? (
                        <Loader2 className="h-3 w-3 animate-spin inline" />
                      ) : (
                        'Submit'
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setShowWriteIn(false);
                        setWriteInValue('');
                        setWriteInGrade(null);
                      }}
                      className="text-xs px-3 py-1 rounded text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowWriteIn(true)}
                  className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Write-in
                </button>
              )}

              {/* Vote weight */}
              <div className="text-xs text-muted-foreground pt-2 border-t border-border">
                Your vote weight: {(ballotData.userVoteWeight * 100).toFixed(2)}% (based on your coopital share)
              </div>
            </>
          ) : null}

          {/* Error display */}
          {error && (
            <div className="text-sm text-red-400 bg-red-500/10 rounded px-3 py-2">
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
